import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  TextField,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { VERIFY_EMAIL, RESEND_VERIFICATION_EMAIL } from '../graphql/mutations';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [verificationComplete, setVerificationComplete] = useState(false);
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [message, setMessage] = useState('');
  const [userData, setUserData] = useState(null);

  // Resend email state
  const [resendEmail, setResendEmail] = useState('');
  const [resendMessage, setResendMessage] = useState('');
  const [resendSuccess, setResendSuccess] = useState(false);

  const [verifyEmail, { loading: verifying }] = useMutation(VERIFY_EMAIL, {
    onCompleted: (data) => {
      setVerificationComplete(true);
      if (data.verifyEmail?.success) {
        setVerificationSuccess(true);
        setMessage(data.verifyEmail.message);

        // If we got a token and user data, auto-login
        if (data.verifyEmail.token && data.verifyEmail.user) {
          setUserData({
            token: data.verifyEmail.token,
            userId: data.verifyEmail.user.id,
            username: data.verifyEmail.user.username,
            email: data.verifyEmail.user.email,
            role: data.verifyEmail.user.role,
            companyId: data.verifyEmail.company?.id,
            companyName: data.verifyEmail.company?.name,
          });

          // Auto-login after 2 seconds
          setTimeout(() => {
            localStorage.setItem('token', data.verifyEmail.token);
            localStorage.setItem('userData', JSON.stringify({
              userId: data.verifyEmail.user.id,
              username: data.verifyEmail.user.username,
              email: data.verifyEmail.user.email,
              role: data.verifyEmail.user.role,
              companyId: data.verifyEmail.company?.id,
              companyName: data.verifyEmail.company?.name,
            }));
            window.location.href = '/';
          }, 2000);
        }
      } else {
        setVerificationSuccess(false);
        setMessage(data.verifyEmail?.message || 'Грешка при потвърждаване на email');
      }
    },
    onError: (err) => {
      setVerificationComplete(true);
      setVerificationSuccess(false);
      setMessage('Грешка при потвърждаване: ' + err.message);
    }
  });

  const [resendVerification, { loading: resending }] = useMutation(RESEND_VERIFICATION_EMAIL, {
    onCompleted: (data) => {
      if (data.resendVerificationEmail?.success) {
        setResendSuccess(true);
        setResendMessage(data.resendVerificationEmail.message);
      } else {
        setResendSuccess(false);
        setResendMessage(data.resendVerificationEmail?.message || 'Грешка при изпращане');
      }
    },
    onError: (err) => {
      setResendSuccess(false);
      setResendMessage('Грешка: ' + err.message);
    }
  });

  useEffect(() => {
    if (token && !verificationComplete) {
      verifyEmail({ variables: { token } });
    }
  }, [token, verificationComplete, verifyEmail]);

  const handleResendSubmit = (e) => {
    e.preventDefault();
    if (!resendEmail.trim()) {
      setResendMessage('Моля, въведете email адрес');
      setResendSuccess(false);
      return;
    }
    setResendMessage('');
    resendVerification({ variables: { email: resendEmail.trim() } });
  };

  // No token provided - show resend form
  if (!token) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ padding: 4, width: '100%', maxWidth: 450 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <EmailIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography component="h1" variant="h5" gutterBottom>
                Потвърждаване на Email
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Въведете вашия email адрес, за да изпратим нов линк за потвърждение.
              </Typography>
            </Box>

            <Box component="form" onSubmit={handleResendSubmit}>
              {resendMessage && (
                <Alert severity={resendSuccess ? "success" : "error"} sx={{ mb: 2 }}>
                  {resendMessage}
                </Alert>
              )}

              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email адрес"
                name="email"
                autoComplete="email"
                autoFocus
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                disabled={resending}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={resending}
              >
                {resending ? <CircularProgress size={24} color="inherit" /> : 'Изпрати нов линк'}
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate('/')}
            >
              Към страницата за вход
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  // Verifying token
  if (verifying || !verificationComplete) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ padding: 4, width: '100%', maxWidth: 400, textAlign: 'center' }}>
            <CircularProgress sx={{ mb: 2 }} size={60} />
            <Typography variant="h6">Потвърждаване на email...</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Моля, изчакайте.
            </Typography>
          </Paper>
        </Box>
      </Container>
    );
  }

  // Verification complete - show result
  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%', maxWidth: 450, textAlign: 'center' }}>
          {verificationSuccess ? (
            <>
              <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography component="h1" variant="h4" color="success.main" gutterBottom>
                Email потвърден!
              </Typography>
              <Alert severity="success" sx={{ mb: 3 }}>
                {message}
              </Alert>
              {userData && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Вашето потребителско име: <strong>{userData.username}</strong>
                  </Typography>
                </Box>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Ще бъдете пренасочени автоматично...
              </Typography>
              <CircularProgress size={24} />
            </>
          ) : (
            <>
              <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
              <Typography component="h1" variant="h5" color="error" gutterBottom>
                Грешка при потвърждаване
              </Typography>
              <Alert severity="error" sx={{ mb: 3 }}>
                {message}
              </Alert>

              <Divider sx={{ my: 3 }} />

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Въведете вашия email, за да изпратим нов линк:
              </Typography>

              <Box component="form" onSubmit={handleResendSubmit}>
                {resendMessage && (
                  <Alert severity={resendSuccess ? "success" : "error"} sx={{ mb: 2 }}>
                    {resendMessage}
                  </Alert>
                )}

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email адрес"
                  name="email"
                  autoComplete="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  disabled={resending}
                  size="small"
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 2, mb: 2 }}
                  disabled={resending}
                >
                  {resending ? <CircularProgress size={24} color="inherit" /> : 'Изпрати нов линк'}
                </Button>
              </Box>

              <Button
                fullWidth
                variant="outlined"
                onClick={() => navigate('/')}
              >
                Към страницата за вход
              </Button>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default VerifyEmail;
